import {
	Paper,
	TableContainer,
	CircularProgress,
	Select,
	MenuItem,
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	IconButton,
	SelectProps,
	SelectChangeEvent,
} from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import BinIcon from '../icons/BinIcon';
import React, { useEffect, useMemo, useState } from 'react';
import { formatCurrency, setDeepValue } from '../../helper/constants';
import { updateQuoteItem } from '../../helper/formatQuoteForUpdate';
import { colours } from '../../shared/colors';
import { useQuoteContext } from '../../shared/contexts/QuoteContext';
import {
	ProductSection,
	QuoteGroupResponse,
	QuotePackage,
	QuoteProduct,
	QuoteProductSection,
	QuoteSectionRiskParameters,
	QuoteSectionRiskTrade,
} from '../../shared/types/quotes';
import { useUpdateQuoteGroupMutation } from '../../services/features/quoteApi/quoteApi';

interface RiskParameterSelectParams {
	disabled: boolean;
	risk: QuoteSectionRiskParameters;
	riskIndex: number;
	product: QuoteProduct | QuotePackage;
	trade: QuoteSectionRiskTrade;
	tradeIndex: number;
}

interface RiskParameterSelectOption {
	id: number;
	value: number;
	label: string;
}

interface ProductExcessSelectParams {
	product: QuoteProduct;
    index: number;
	section: ProductLimitProductSection;
	onChange: (e: SelectChangeEvent<any>) => {};
    disabled?: boolean;
}

const ProductExcessSelect = ({
	product,
    index,
	section,
    disabled,
	onChange,
}: ProductExcessSelectParams) => {
	const options = product.quoteProductSections[index].availableExcesses?.map((e) => e.value);

	return (
		<Select
			size='small'
			disabled={disabled || (options ? options.length <= 1 : true)}
			value={product.quoteProductSections[index].excess}
			renderValue={(item) => <div>{formatCurrency(item).split('.')[0]}</div>}
			onChange={onChange}
		>
			{(options ?? section.excess)
				.slice()
				.sort((n1, n2) => n1 - n2)
				.map((value: any) => {
					return (
						<MenuItem value={value} key={`${product.name}-excess-${value}`}>
							{formatCurrency(value).split('.')[0]}
						</MenuItem>
					);
				})}
		</Select>
	);
};

const RiskParameterSelect = ({
	risk,
	riskIndex,
	trade,
	tradeIndex,
	disabled,
	product,
}: RiskParameterSelectParams) => {
	const { quoteGroup } = useQuoteContext();

	const [updateQuoteGroup] = useUpdateQuoteGroupMutation();

	const handleUpdate = async (value: any) => {
		const newProduct = setDeepValue(
			product,
			`quoteProductSections[0].quoteSectionRiskTrades[${tradeIndex}].quoteSectionRiskParameters[${riskIndex}].value`,
			value
		);
		// console.log('New product risk trades', newProduct);

		if (quoteGroup) {
			const update = updateQuoteItem(quoteGroup, newProduct);
			// console.log('Here is the update', update);
			await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
				.unwrap()
				.catch((error) => console.log('Error in updating quote items', error));
		}
	};

	const riskValues: RiskParameterSelectOption[] = (
		risk.riskParameterAvailableValues.length > 0
			? [
					{
						id: -1,
						value: 0,
						label: 'None',
					},
					...risk.riskParameterAvailableValues.map((item) => ({
						id: item.id,
						value: item.value,
						label: formatCurrency(item.value).split('.')[0],
					})),
			  ]
			: []
	)
		.slice()
		.sort((n1, n2) => n1.value - n2.value);

	return (
		<Select
			size='small'
			disabled={disabled || risk.riskParameterAvailableValues.length <= 1}
			value={risk.value}
			renderValue={(item) => <div>{formatCurrency(item).split('.')[0]}</div>}
			onChange={(e) => handleUpdate(e.target.value)}
		>
			{riskValues.map((item) => {
				return (
					<MenuItem
						value={item.value}
						key={`${trade.name}-${risk.name}-${item.id}-${item.value}`}
					>
						{item.label}
					</MenuItem>
				);
			})}
		</Select>
	);
};

const RiskOptionSelect = ({
	risk,
	riskIndex,
	trade,
	tradeIndex,
	disabled,
	product,
}: RiskParameterSelectParams) => {
	const { quoteGroup } = useQuoteContext();

	const [updateQuoteGroup] = useUpdateQuoteGroupMutation();

	const handleUpdate = async (value: any) => {
		/// TODO: So far there is no place to update this value, only display
		// const newProduct = setDeepValue(
		// 	product,
		// 	`quoteProductSections[0].quoteSectionRiskTrades[${tradeIndex}].quoteSectionRiskParameters[${riskIndex}].value`,
		// 	value
		// );
		// console.log('New product risk trades', newProduct);
		// if (quoteGroup) {
		// 	const update = updateQuoteItem(quoteGroup, newProduct);
		// 	console.log('Here is the update', update);
		// 	await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
		// 		.unwrap()
		// 		.catch((error) => console.log('Error in updating quote items', error));
		// }
	};

	const riskOptions: RiskParameterSelectOption[] = (
		risk.riskParameterOptions.length > 0
			? [
					...risk.riskParameterOptions.map((item) => ({
						id: item.id,
						value: item.value,
						label: formatCurrency(item.value).split('.')[0],
					})),
			  ]
			: []
	)
		.slice()
		.sort((n1, n2) => n1.value - n2.value);

	return (
		<Select
			size='small'
			disabled={disabled || risk.riskParameterOptions.length <= 1}
			value={riskOptions[0]?.value}
			renderValue={(item) => <div>{formatCurrency(item).split('.')[0]}</div>}
			onChange={(e) => handleUpdate(e.target.value)}
		>
			{riskOptions.map((item) => {
				return (
					<MenuItem
						value={item.value}
						key={`${trade.name}-${risk.name}-${item.id}-${item.value}`}
					>
						{item.label}
					</MenuItem>
				);
			})}
		</Select>
	);
};

/**
 * Component for rendering the table header of the product limit table.
 *
 * @returns {JSX.Element} The JSX element for the table header.
 */
const ProductLimitTableHead = () => {
	return (
		<TableHead
			sx={{
				backgroundColor: '#f9f9f9',
			}}
		>
			<TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 700, fontSize: '12px' } }}>
				<TableCell padding='checkbox'></TableCell>
				<TableCell>PRODUCT</TableCell>
				<TableCell align='center'>LIMIT OF INDEMNITY</TableCell>
				<TableCell align='center'>RETENTION</TableCell>
				<TableCell align='center'>BASE PREMIUM</TableCell>
			</TableRow>
		</TableHead>
	);
};

interface ProductLimitProductSection {
	liability: number[];
	excess: number[];
	premium: number;
}

/**
 * Interface for ProductLimitOptions.
 *
 * @interface ProductLimitOptions
 */
interface ProductLimitOptions {
	id: string;
	productSections: ProductLimitProductSection[];
}

interface DisplayProductRow {
	packageName: string;
	packageId: string;
	products: QuoteProduct[];
}

/**
 * Component for rendering the product limit table.
 *
 * @param {Object} props - The component's props.

 * @param {Function} props.removeProduct - A function to remove a product.
 * @param {Function} props.removePackage - A function to remove a package.
 * @param {Function} props.checkBoxHandler - A function to handle checkbox changes.
 *
 * @returns {JSX.Element} The JSX element for the product limit table.
 */
export const ProductLimitTable = (props: any) => {
	const formik = props.options;

	const {
		quoteGroup,
		loadingProducts,
        loading,
		setLoadingProducts,
		currentQuotes,
		isReferred,
		isDeclined,
		isReferralApproved,
		quoteCheckbox,
	} = useQuoteContext();

	const readonly = isReferralApproved || isReferred || isDeclined;
    const quoteLoading = loading || loadingProducts;

	const [updateQuoteGroup] = useUpdateQuoteGroupMutation();

	const productLimitOptions: ProductLimitOptions[] = useMemo(
		() => formik.values.products.concat(formik.values.packages.flatMap((p: any) => p.products)),
		[formik.values.products, formik.values.packages]
	);

    const productsArray = currentQuotes?.flatMap((q) => q.quoteProducts);
	const packagesArray = currentQuotes?.flatMap((q) => q.quotePackages);

	const displayProductRows = useMemo(() => {
		const temp = [
			...(productsArray.length > 0
				? [
						{
							packageName: 'Products',
							packageId: 'singleProducts',
							products: [...productsArray],
						},
				  ]
				: []),
			...packagesArray.map((package1) => ({
				packageName: package1.name,
				packageId: package1.id.toString(),
				products: [...package1.quoteProducts],
			})),
		];
		// console.log("DISPLAY PRODUCT ROWS", temp);
		return temp.filter((package1) => (package1.packageId ?? '').length > 0);
	}, [productsArray, packagesArray]);

	/**
	 * Update the quote items with the new value.
	 *
	 * @param {Object} productSelected - The selected product.
	 * @param {number} valueToChange - The new value to set.
	 * @param {string} paramName - The name of the parameter to update.
	 * @param {Object} package1 - The package data.
	 */
	const updateQuoteItems = async (
		productSelected: any,
		valueToChange: any,
		paramName: string,
		package1: any
	) => {
		setLoadingProducts(true);
		let temp: QuoteProduct | QuotePackage | undefined;
		if (quoteGroup) {
			const productsTemp = quoteGroup.quotes.flatMap((q) => q.quoteProducts);
			const packagesTemp = quoteGroup.quotes.flatMap((q) => q.quotePackages);
			if (package1.packageId === 'singleProducts') {
				temp = productsTemp.find((p) => p.id == productSelected.id);
				if (temp !== undefined && temp.quoteProductSections) {
					let newProduct = {
						...temp,
						quoteProductSections: [
							{
								...temp.quoteProductSections[0],
								[paramName]: valueToChange,
							},
						],
					};
					// temp.quoteProductSections[0][paramName] = valueToChange;
					const update = updateQuoteItem(quoteGroup, newProduct);
					await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
						.unwrap()
						.catch((error) => console.log('Error in updating quote items', error));
				}
			} else {
				temp = packagesTemp.find((p) => p.id == package1.packageId);
				if (temp !== undefined && temp.quoteProducts) {
					let newPackage = {
						...temp,
						quoteProducts: temp.quoteProducts.map((prod) => {
							return {
								...prod,
								quoteProductSections:
									prod.id == productSelected.id
										? [
												{
													...prod.quoteProductSections[0],
													[paramName]: valueToChange,
												},
										  ]
										: prod.quoteProductSections,
							};
						}),
					};
					const update = updateQuoteItem(quoteGroup, newPackage);
					await updateQuoteGroup({ quote: update, currentQuote: quoteGroup })
						.unwrap()
						.catch((error) => console.log('Error in updating quote items', error));
				}
			}
		}
		setLoadingProducts(false);
	};

	/**
	 * Generate a row for the product in the table.
	 *
	 * @param {Object} product - The product data.
	 * @param {Object} package1 - The package data.
	 * @returns {JSX.Element} The JSX element for the product row.
	 */
	const generateProductRow = (product: QuoteProduct, package1: DisplayProductRow) => {
		const productOption = productLimitOptions.find((p: any) => p.id == product.id);
		const productSection = (productOption?.productSections ?? []).slice()[0];
		const section = {
			...(productSection ?? {}),
			liability:
				productSection?.liability ?? [].length > 0
					? productSection.liability
					: [product.quoteProductSections[0].liability],
			excess:
				productSection?.excess ?? [].length > 0
					? productSection.excess
					: [product.quoteProductSections[0].excess],
		};

		const riskTrades = product.quoteProductSections
			.flatMap((p) => p.quoteSectionRiskTrades)
			.filter(Boolean) as QuoteSectionRiskTrade[];

		// value will be 0 and riskParameterAvailableValues will be empty
		// if value is 0 then option is not selected
		// add option for not choosing option - value = 0
		// if riskParameterOptions is not empty, display value of the first item
		return (
			<React.Fragment key={`table-fragment-${package1.packageId}-${product.id}`}>
				<TableRow
					hover
					role='checkbox'
					key={`table-${package1.packageId}-${product.id}`}
					sx={{
						'& .MuiTableCell-body': {
							fontWeight: 400,
							fontSize: '14px',
						},
					}}
				>
					<TableCell>
						{package1.packageId === 'singleProducts' ? (
							loadingProducts ? (
								<CircularProgress size='1em' sx={{ color: colours.primary.main }} />
							) : (
								<IconButton
									aria-label='delete'
									size='small'
									disabled={
										readonly || quoteLoading || product?.isMainForPackage
									}
									onClick={() => props.removeProduct(product.id)}
								>
									<BinIcon
										fill={colours.primaryDark.main}
										style={{ color: colours.primaryDark.main }}
										sx={{ fontSize: '1em' }}
									/>
								</IconButton>
							)
						) : (
							<Checkbox
								sx={{
									color: '#474848',
									'&.Mui-checked': {
										color: '#474848',
									},
									'&.Mui-disabled': {
										color: 'rgba(182, 182, 182, 1)',
										pointerEvents: 'auto',
										'&:hover': {
											backgroundColor: 'transparent',
										},
										cursor: 'not-allowed',
										'& .MuiSvgIcon-root .innerBox': {
											fill: '#eee',
										},
									},
								}}
								checked={
									quoteCheckbox.find(
										(x: any) =>
											x.id === product?.id &&
											x.packageId === package1.packageId
									) == null
								}
								disabled={
									readonly ||
									quoteLoading ||
									(product?.isMainForPackage ?? false)
								}
								onClick={() =>
									props.checkBoxHandler(product?.id, package1.packageId)
								}
							/>
						)}
					</TableCell>
					<TableCell component='th' scope='row' padding='none'>
						{product.name}
					</TableCell>
					<TableCell>
						<Select
							size='small'
							disabled={
								readonly ||
								quoteLoading ||
								productSection == null ||
								section.liability.length === 1
							}
							value={product.quoteProductSections[0].liability}
							renderValue={(item) => <div>{formatCurrency(item).split('.')[0]}</div>}
							onChange={(e: any) => {
								updateQuoteItems(product, e.target.value, 'liability', package1);
							}}
						>
							{section.liability
								.slice()
								.sort((n1, n2) => n1 - n2)
								.map((value: any) => {
									return (
										<MenuItem
											value={value}
											key={`${product.name}-liability-${value}`}
										>
											{formatCurrency(value).split('.')[0]}
										</MenuItem>
									);
								})}
						</Select>
					</TableCell>
					<TableCell>
						<ProductExcessSelect
							{...{
								product,
								section,
                                index: 0,
                                disabled: readonly || quoteLoading,
								onChange: (e) =>
									updateQuoteItems(product, e.target.value, 'excess', package1),
							}}
						/>
					</TableCell>
					<TableCell
						style={{
							textAlign: loadingProducts ? 'center' : 'right',
							paddingRight: '1em',
						}}
					>
						{!loadingProducts &&
							(isReferred
								? 'Referred'
								: isDeclined
								? 'Declined'
								: formatCurrency(
										product.quoteProductSections[0].totals?.basePremium ?? 0
								  ))}

						{loadingProducts && (
							<CircularProgress
								size={30}
								sx={{
									color: colours.primary.main,
									position: 'absolute',
									marginTop: -2,
									marginLeft: -1,
									zIndex: 1,
								}}
							/>
						)}
					</TableCell>
				</TableRow>
				{riskTrades.map((trade, tradeIndex) =>
					trade.quoteSectionRiskParameters
						.filter(
							(risk) => !(risk.riskParameterAvailableValues.length === 0 && risk.value === 0)
						)
						.map((risk, riskIndex) => {
							return (
								<TableRow key={`${product.id}-trade-${trade.id}-${risk.id}`}>
									<TableCell></TableCell>
									<TableCell padding='none'>{risk.name}</TableCell>
									<TableCell>
										<RiskParameterSelect
											disabled={readonly || quoteLoading}
											{...{ trade, risk, riskIndex, tradeIndex, product }}
										/>
									</TableCell>
									<TableCell>
										<RiskOptionSelect
											disabled={readonly || quoteLoading}
											{...{ trade, risk, riskIndex, tradeIndex, product }}
										/>
									</TableCell>
									<TableCell></TableCell>
								</TableRow>
							);
						})
				)}
			</React.Fragment>
		);
	};

	return (
		<TableContainer
			sx={{
				border: '1px solid rgba(224, 224, 224, 1)',
				borderRadius: '4px',
			}}
		>
			<Table>
				<ProductLimitTableHead />
				{displayProductRows.map((package1) => {
					return (
						<TableBody key={`package-${package1.packageId}`}>
							{package1.packageId !== 'singleProducts' && (
								<TableRow>
									<TableCell>
										{package1.packageId !== 'singleProducts' && //&& props.selectedPackages.length > 1
											(loadingProducts ? (
												<CircularProgress
													size='1em'
													sx={{ color: colours.primary.main }}
												/>
											) : (
												<IconButton
													aria-label='delete'
													size='small'
													disabled={readonly || quoteLoading}
													onClick={() =>
														props.removePackage(package1.packageId)
													}
												>
													<BinIcon
														fill={colours.primaryDark.main}
														style={{
															color: colours.primaryDark.main,
														}}
														sx={{ fontSize: '1em' }}
													/>
												</IconButton>
											))}
									</TableCell>
									<TableCell
										style={{
											textDecoration: 'underline',
											fontSize: 16,
										}}
										component='th'
										scope='row'
										padding='none'
										colSpan={4}
									>
										{package1?.packageName}
									</TableCell>
								</TableRow>
							)}
							{package1?.products.map((product) =>
								generateProductRow(product, package1)
							)}
						</TableBody>
					);
				})}
			</Table>
		</TableContainer>
	);
};
